import React from "react"
import cc from "classcat"
import Slider from "react-slick"
import Button from "components/buttons/Button"
import kebabCase from "lodash.kebabcase"
import useTranslations from "hooks/useTranslations"
import CustomSliderDot from "components/atoms/common/CustomSliderDot"

type Testimonial = {
  title: string
  fact_large: string
  fact_small: string
  id: string
  theme: Theme
}

type Theme = "green" | "yellow" | "black" | "light"

const TestimonialItem = ({ title, fact_large, fact_small, id, theme }) => {
  const { testimonialReadMore } = useTranslations()
  return (
    <div className="mb-6 flex flex-col space-y-12 pr-8 md:mb-0 md:px-16 md:first:pl-0 md:last:pr-0">
      <div
        className={cc([
          "grow space-y-6",
          {
            "text-white": theme === "black",
            "text-zinc-800": ["green", "yellow", "light"].includes(theme),
          },
        ])}
      >
        <div
          className={cc([
            "font-display text-5xl underline",
            {
              "text-zinc-700": theme === "light",
              "text-white": ["green", "yellow"].includes(theme),
            },
          ])}
        >
          {fact_large}
        </div>
        <div className="font-display text-2xl">{fact_small}</div>
        <h3 className="m-0 font-display text-lg leading-6">{title}</h3>
      </div>
      <div className="flex flex-col items-start">
        <Button
          bgStyle={theme === "black" ? "light" : "black"}
          label={testimonialReadMore}
          link={`/testimonials/${kebabCase(id)}`}
        />
      </div>
    </div>
  )
}

type TestimonialsProps = {
  list: Testimonial[]
  className?: string
  bgColor?: Theme
}

export default function Testimonials({
  list = [],
  className = "",
  bgColor = "green",
}: TestimonialsProps) {
  const sliderSettings = {
    infinite: true,
    slidesToScroll: 1,
    speed: 1000,
    autoplaySpeed: 3000,
    autoplay: true,
    dots: true,
    arrows: false,
    fade: false,
    dotsClass: `slick-dots ${
      bgColor === "black" ? "green-on-black" : "black-on-white"
    }`,
    customPaging: () => (
      <a>
        <CustomSliderDot outerCircleColor="white" />
      </a>
    ),
  }

  return (
    <div
      className={cc([
        "relative py-16 lg:py-24",
        className,
        {
          "bg-kkm-green": bgColor === "green",
          "bg-kkm-yellow": bgColor === "yellow",
          "bg-zinc-800": bgColor === "black",
          "bg-zinc-50": bgColor === "light",
        },
      ])}
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-lg md:mx-0 md:max-w-none">
          <div
            className={cc([
              "hidden grid-cols-2 divide-x md:grid",
              bgColor === "light" ? "divide-kkm-green" : "divide-white",
            ])}
          >
            {list.map((testimonial) => (
              <TestimonialItem
                key={testimonial.id}
                {...testimonial}
                theme={bgColor}
              />
            ))}
          </div>
          <div className="block md:hidden">
            <Slider slidesToShow={1} {...sliderSettings}>
              {list.map((testimonial) => (
                <TestimonialItem
                  key={testimonial.id}
                  {...testimonial}
                  theme={bgColor}
                />
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </div>
  )
}
